import { useState } from "react";
import { toast } from "sonner";
import { Mail, MessageCircle, Phone, Trash2, X } from "lucide-react";
import { api, useApi, waLink, formatApiError } from "@/lib/api";
import { AdminHeader } from "@/pages/admin/AdminLayout";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";

const STATUSES = [["new", "New"], ["contacted", "Contacted"], ["follow_up", "Follow up"], ["closed", "Closed"]];
const STATUS_STYLE = { new: "bg-forest text-white", contacted: "bg-sky-100 text-sky-900", follow_up: "bg-amber-100 text-amber-900", closed: "bg-clay text-ink/70" };
const statusOf = (q) => q.status || "new";
const fmt = (d) => new Date(d).toLocaleString([], { dateStyle: "medium", timeStyle: "short" });

const StatusSelect = ({ q, onChange }) => (
  <Select value={statusOf(q)} onValueChange={onChange}>
    <SelectTrigger className={`h-9 w-36 rounded-full border-0 text-xs font-bold ${STATUS_STYLE[statusOf(q)]}`} data-testid={`inquiry-status-${q.id}`}><SelectValue /></SelectTrigger>
    <SelectContent>
      {STATUSES.map(([v, l]) => <SelectItem key={v} value={v} data-testid={`inquiry-status-option-${v}`}>{l}</SelectItem>)}
    </SelectContent>
  </Select>
);

const ContactLinks = ({ q }) => {
  const digits = (q.phone || "").replace(/\D/g, "");
  const link = "inline-flex items-center gap-1.5 font-semibold text-forest underline-offset-4 hover:underline";
  return (
    <div className="mt-1 flex flex-wrap gap-x-5 gap-y-1 text-sm">
      <a href={`mailto:${q.email}?subject=${encodeURIComponent(`Re: ${q.listing_title || "Escamequita"}`)}`} className={link} data-testid={`inquiry-email-${q.id}`}><Mail className="h-4 w-4" /> {q.email}</a>
      {digits && <a href={waLink(digits, `Hi ${q.first_name}, thanks for your inquiry about ${q.listing_title || "Escamequita"}.`)} target="_blank" rel="noreferrer" className={link} data-testid={`inquiry-whatsapp-${q.id}`}><MessageCircle className="h-4 w-4" /> WhatsApp {q.phone}</a>}
      {digits && <a href={`tel:+${digits}`} className={link} data-testid={`inquiry-call-${q.id}`}><Phone className="h-4 w-4" /> Call</a>}
    </div>
  );
};

const Notes = ({ q, onChanged }) => {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const add = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try {
      await api.post(`/admin/inquiries/${q.id}/notes`, { text });
      setText("");
      toast.success("Note saved");
      onChanged();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };
  const remove = async (n) => {
    if (!window.confirm("Delete this note?")) return;
    await api.delete(`/admin/inquiries/${q.id}/notes/${n.id}`);
    onChanged();
  };
  return (
    <div className="mt-5 rounded-xl bg-linen p-4" data-testid={`inquiry-notes-${q.id}`}>
      <p className="text-xs font-bold uppercase tracking-[0.2em] text-sage">Notes</p>
      {(q.notes || []).map((n) => (
        <div key={n.id} className="mt-3 flex gap-3 border-l-2 border-sage pl-3" data-testid={`inquiry-note-${n.id}`}>
          <div className="flex-1">
            <p className="text-[11px] font-bold text-moss">{fmt(n.created_at)}</p>
            <p className="whitespace-pre-line text-sm text-ink">{n.text}</p>
          </div>
          <button onClick={() => remove(n)} className="self-start p-1 text-moss hover:text-destructive" aria-label="Delete note" data-testid={`inquiry-note-delete-${n.id}`}><X className="h-4 w-4" /></button>
        </div>
      ))}
      <div className="mt-3 flex flex-col gap-2 sm:flex-row">
        <Textarea rows={2} placeholder="Add a note (e.g. called, wants a tour Friday)…" value={text} onChange={(e) => setText(e.target.value)} className="bg-white" data-testid={`inquiry-note-input-${q.id}`} />
        <button onClick={add} disabled={busy || !text.trim()} className="btn-primary shrink-0 self-end disabled:opacity-50" data-testid={`inquiry-note-save-${q.id}`}>Save note</button>
      </div>
    </div>
  );
};

const InquiryCard = ({ q, reload }) => {
  const setStatus = async (status) => {
    await api.patch(`/admin/inquiries/${q.id}`, { status });
    toast.success("Status updated");
    reload();
  };
  const remove = async () => {
    if (!window.confirm("Delete this inquiry?")) return;
    await api.delete(`/admin/inquiries/${q.id}`);
    toast.success("Deleted");
    reload();
  };
  return (
    <div className={`rounded-2xl border bg-white p-6 ${statusOf(q) === "new" ? "border-forest" : "border-clay"}`} data-testid={`inquiry-row-${q.id}`}>
      <div className="flex flex-col justify-between gap-3 sm:flex-row">
        <div className="min-w-0">
          <p className="font-bold text-ink" data-testid={`inquiry-name-${q.id}`}>{q.first_name} {q.last_name}</p>
          <ContactLinks q={q} />
          <p className="mt-1 text-xs font-bold text-sage">{q.listing_title || "General inquiry"} · {fmt(q.created_at)}</p>
        </div>
        <div className="flex shrink-0 items-start gap-2">
          <StatusSelect q={q} onChange={setStatus} />
          <button onClick={remove} className="rounded-lg border border-clay p-2 text-moss hover:text-destructive" aria-label="Delete" data-testid={`inquiry-delete-${q.id}`}><Trash2 className="h-4 w-4" /></button>
        </div>
      </div>
      <p className="mt-4 whitespace-pre-line text-sm leading-relaxed text-ink">{q.message}</p>
      <Notes q={q} onChanged={reload} />
    </div>
  );
};

export default function AdminInquiries() {
  const { data, reload } = useApi("/admin/inquiries", []);
  const [filter, setFilter] = useState("all");
  const tabs = [["all", "All"], ...STATUSES];
  const count = (v) => (v === "all" ? data.length : data.filter((q) => statusOf(q) === v).length);
  const items = filter === "all" ? data : data.filter((q) => statusOf(q) === filter);
  return (
    <div data-testid="admin-inquiries">
      <AdminHeader title="Inquiries" />
      <div className="mb-6 flex flex-wrap gap-2" data-testid="inquiry-filters">
        {tabs.map(([v, l]) => (
          <button key={v} onClick={() => setFilter(v)} data-testid={`inquiry-filter-${v}`}
            className={`rounded-full px-4 py-2 text-sm font-bold transition-colors duration-200 ${filter === v ? "bg-forest text-white" : "border border-clay bg-white text-ink/75 hover:bg-linen"}`}>
            {l} <span className="opacity-70">({count(v)})</span>
          </button>
        ))}
      </div>
      {items.length === 0 && <p className="rounded-2xl border border-clay bg-white p-8 text-moss" data-testid="inquiries-empty">{data.length ? "No inquiries with this status." : "No inquiries yet. Messages from the contact forms appear here."}</p>}
      <div className="space-y-4">{items.map((q) => <InquiryCard key={q.id} q={q} reload={reload} />)}</div>
    </div>
  );
}
